import { Suspense, lazy } from "react";
import { SEOHead } from "@/components/SEOHead";
import { HomepageTiles } from "@/components/HomepageTiles";
import { SocialProofBar } from "@/components/SocialProofBar";

import { GlobalLoadingFallback, CardSkeleton } from "@/components/ui/loading-fallback";
import { createWebsiteStructuredData, createOrganizationStructuredData } from "@/utils/structuredData";

// Lazy load everything below the fold
const FlashNews = lazy(() => import("@/components/FlashNews"));
const TopUsers = lazy(() => import("@/components/TopUsers").then(m => ({ default: m.TopUsers })));
const SocialMediaFeed = lazy(() => import("@/components/SocialMediaFeed"));
const UpcomingRaces = lazy(() => import("@/components/UpcomingRaces").then(m => ({ default: m.UpcomingRaces })));
const PollWidget = lazy(() => import("@/components/PollWidget").then(m => ({ default: m.PollWidget })));
const SyndicateResultsTicker = lazy(() => import("@/components/SyndicateResultsTicker").then(m => ({ default: m.SyndicateResultsTicker })));

const RacingCalendar2026 = lazy(() => import("@/components/RacingCalendar2026").then(m => ({ default: m.RacingCalendar2026 })));
const ComparisonTable = lazy(() => import("@/components/ComparisonTable").then(m => ({ default: m.ComparisonTable })));
const SEOArticles = lazy(() => import("@/components/SEOArticles").then(m => ({ default: m.SEOArticles })));
const IntroductionText = lazy(() => import("@/components/IntroductionText").then(m => ({ default: m.IntroductionText })));
const HomepageShopBanner = lazy(() => import("@/components/HomepageShopBanner").then(m => ({ default: m.HomepageShopBanner })));


const IndexContent = () => {
  const websiteStructuredData = createWebsiteStructuredData();
  const organizationStructuredData = createOrganizationStructuredData();

  const combinedStructuredData = [
    websiteStructuredData,
    organizationStructuredData
  ];

  return (
    <>
      <SEOHead
        title="Horsebeat.cz – Spolumajitelství dostihových koní (syndikát) a statistiky"
        description="🐴 Spolumajitelství dostihových koní pro fanoušky i začátečníky 📊 Statistiky koní a žokejů 🏆 Zažijte dostihy naplno s Horsebeat.cz – vaše brána do světa dostihového sportu!"
        keywords="spolumajitelství dostihového koně, dostihový syndikát, podíl v koni, dostihové statistiky, statistiky koní, žokejové, Velká Pardubická, Praha dostihy, Pardubice závodiště, galop překážky, dostihový sport Česká republika, dostihová komunita, zážitky z dostihů"
        pageType="homepage"
        structuredData={combinedStructuredData}
      />
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 py-4 md:py-6">

        {/* Editorial hero band */}
        <section className="mb-3 md:mb-4 border-b border-border pb-2 md:pb-3">
          <div className="grid md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-9">
              <h1 className="display-xl text-foreground text-balance">
                Staňte se <span className="text-accent">spolumajitelem dostihového koně</span>. Zblízka, s partou fanoušků.
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <a
                  href="/syndicates#nabidka-clenstvi"
                  className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
                >
                  Vybrat koně od 2 490 Kč
                </a>
                <a
                  href="https://www.youtube.com/watch?v=N_96RcMl0F0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent underline underline-offset-4"
                >
                  ▶ Jak to funguje (60 s)
                </a>
                <span className="text-xs text-muted-foreground">
                  Členství na sezónu 2027
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">
                  ✓ Jednorázová platba, žádné předplatné
                </span>
              </div>
            </div>
            <div className="md:col-span-3 md:text-right">
              <p className="text-sm text-muted-foreground leading-relaxed font-body max-w-xs md:ml-auto">
                A k tomu tipy, statistiky a databáze českých dostihů — zdarma pro všechny.
              </p>
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <SocialProofBar />

        {/* Ticker s posledními výsledky našich koní */}
        <Suspense fallback={null}>
          <SyndicateResultsTicker />
        </Suspense>


        {/* Homepage Tiles - Spolumajitelství (hlavní) + Tipy */}
        <HomepageTiles />
        
        {/* Flash News and Top Users */}
        <div className="mb-4 md:mb-5 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-2">
            <Suspense fallback={<CardSkeleton />}>
              <FlashNews />
            </Suspense>
          </div>
          <div>
            <Suspense fallback={<CardSkeleton />}>
              <TopUsers />
            </Suspense>
          </div>
        </div>

        {/* Shop banner — přesunuto pod fold */}
        <div className="mb-4 md:mb-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <HomepageShopBanner />
          </Suspense>
        </div>

        {/* Below-fold sections with content-visibility for rendering performance */}
        <div className="mb-4 md:mb-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <SocialMediaFeed />
          </Suspense>
        </div>
        
        <div className="mb-4 md:mb-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <UpcomingRaces />
          </Suspense>
        </div>
        
        <div className="mb-4 md:mb-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <PollWidget />
          </Suspense>
        </div>


        <div className="mb-4 md:mb-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <RacingCalendar2026 />
          </Suspense>
        </div>

        <div className="mt-5 md:mt-6 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <SEOArticles />
          </Suspense>
        </div>
        
        <div className="mt-4 md:mt-5 cv-auto">
          <Suspense fallback={<CardSkeleton />}>
            <ComparisonTable />
          </Suspense>
        </div>
      </main>

    </div>
    </>
  );
};

const Index = () => {
  return (
    <Suspense fallback={<GlobalLoadingFallback />}>
      <IndexContent />
    </Suspense>
  );
};

export default Index;
